import { action, makeObservable, computed, observable, override } from 'mobx'
import ModalViewModel from '../../../../infra/ModalViewModel'
import { Id } from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import InventoryStore from 'admin/InventoryStore'
import InventoryItemRequestQueryFilterBuilder, {
  InventoryItemArchivedFilterOptions,
} from 'admin/inventory/InventoryItemRequestQueryFilterBuilder'
import InventoryItem, {
  InventoryItemPricing,
} from 'admin/inventory/InventoryItem'
import { ViewModelList } from 'infra/ViewModelList'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { switchMap } from 'rxjs'
import { shareLatest } from 'utils/rx-util'

export default class ManageItemsInBundleViewModel extends ModalViewModel {
  readonly serviceItemVms: ViewModelList<InventoryItem, IncludedItemViewModel>

  /**
   * Serch term for item selection
   */
  searchText = new SearchTextViewModel()

  /**
   * The current workspace id that the user has picked.
   */
  @observable
  workspaceId = ''

  /**
   * The base inventory item id for the Bundle.
   */
  @observable
  baseItemId = ''

  /**
   * The saved results from includedItemViewModels.
   */
  @observable
  includedItems: IncludedItemViewModel[] = []

  constructor(
    private setIncludedItems: (items: IncludedItemViewModel[]) => void,
    private inventoryStore: InventoryStore,
  ) {
    super()
    makeObservable(this)

    this.serviceItemVms = new ViewModelList({
      models: () => this.inventoryScrollProvider.items.slice(),
      create: (model) =>
        new IncludedItemViewModel(
          this.includedItems.some(
            (includedItem) => includedItem.inventoryItemId === model.id,
          ),
          model.id === this.baseItemId,
          model.id,
          model.versionId,
          model?.name || '',
          model?.price || 0,
          model?.proPrice || 0,
          model?.description || '',
          model?.workspace?.id || Id.none,
          this.includedItems.find((i) => i.inventoryItemId === model.id)
            ?.includedQuantity || 0,
          model?.archived,
          this.handleIncludedChange,
        ),
    })

    this.close$.subscribe(this.reset)
    this.query$.pipe(this.whileShowing()).subscribe()
  }

  @computed
  get isWorkspaceIdSet() {
    return this.workspaceId !== ''
  }

  @computed
  get totalSelected() {
    return this.includedItems.length
  }

  @computed
  get isBaseItemToggled() {
    return this.includedItems.some((item) => item.isBaseItem)
  }

  @computed
  get serviceItems() {
    return this.serviceItemVms.items
  }

  @action.bound
  setWorkspaceId(workspaceId: string) {
    this.workspaceId = workspaceId
  }

  @action.bound
  setBaseItemId(baseItemId: string) {
    this.baseItemId = baseItemId
  }

  @action.bound
  setCurrentlyIncludedItems(items: IncludedItemViewModel[]) {
    this.includedItems = items
  }

  @action.bound
  submit() {
    const baseItem = this.includedItems.find((item) => item.isBaseItem === true)

    this.baseItemId = baseItem?.inventoryItemId || ''

    const onlyIncludedItems = this.includedItems.filter(
      (item) => item.isIncludedItem && !item.isBaseItem,
    )

    const filteredItems = (
      baseItem ? [baseItem, ...onlyIncludedItems] : onlyIncludedItems
    ).filter((i) => i.includedQuantity > 0)

    this.setIncludedItems(filteredItems)
    this.close()
  }

  @override
  show() {
    this.activate()
    super.show()
  }

  @action.bound
  reset() {
    this.searchText.clear()
    this.serviceItemVms.deactivate()
  }

  inventoryScrollProvider = new InfiniteScrollProvider<InventoryItem>({
    limit: 100,
    startFetchTaskAsPending: true,
    fetchItems: (paging) => {
      return this.inventoryStore.fetchInventoryItems(
        this.workspaceId ? [this.workspaceId] : [],
        paging.limit ?? 100,
        this.searchText.value,
        paging.after ?? undefined,
        new InventoryItemRequestQueryFilterBuilder()
          .withPriceTypes([InventoryItemPricing.Flat])
          .withArchivedStatus(InventoryItemArchivedFilterOptions.ACTIVE)
          .build(),
        'name',
      )
    },
  })

  /**
   * Server query pipeline.
   */
  query$ = this.searchText.debouncedValue$.pipe(
    switchMap(() => this.inventoryScrollProvider.fetch()),
    shareLatest(),
  )

  activate() {
    this.inventoryScrollProvider.fetch()
    this.serviceItemVms.activate()
  }

  private handleIncludedChange = (item: IncludedItemViewModel) => {
    // Keep the included except the item being modified.
    // We do this so that we prevent duplicates in case base item is removed but the item is still included
    const includedExceptModifiedItem = this.includedItems.filter(
      (i) => i.inventoryItemId !== item.inventoryItemId,
    )
    if (item.isIncludedItem) {
      this.includedItems = [item, ...includedExceptModifiedItem]
    } else {
      this.includedItems = includedExceptModifiedItem
    }
  }
}

export class IncludedItemViewModel {
  @observable isIncludedItem: boolean = false
  @observable isBaseItem: boolean = false
  @observable inventoryItemId: Id = Id.none
  @observable inventoryItemVersionId = Id.none
  @observable name = ''
  @observable price = 0
  @observable proPrice = 0
  @observable description = ''
  @observable workspaceId = Id.none
  @observable includedQuantity = 0
  @observable archived = false

  constructor(
    isIncludedItem: boolean,
    isBaseItem: boolean,
    inventoryItemId: Id,
    inventoryItemVersionId: Id,
    name: string,
    price: number,
    proPrice: number,
    description: string,
    workspaceId: Id,
    includedQuantity: number,
    archived: boolean,
    private onIncludedChange?: (item: IncludedItemViewModel) => void,
  ) {
    makeObservable(this)
    this.isIncludedItem = isIncludedItem
    this.isBaseItem = isBaseItem
    this.inventoryItemId = inventoryItemId
    this.inventoryItemVersionId = inventoryItemVersionId
    this.name = name
    this.price = price
    this.proPrice = proPrice
    this.description = description
    this.workspaceId = workspaceId
    this.includedQuantity = includedQuantity
    this.archived = archived
  }

  @action.bound
  toggleBaseItem() {
    this.isBaseItem = !this.isBaseItem

    // Also toggle as included item for convenience
    if (!this.isIncludedItem) {
      this.isIncludedItem = !this.isIncludedItem
    }

    if (
      (this.isIncludedItem && this.includedQuantity === 0) ||
      this.isBaseItem
    ) {
      this.includedQuantity = 1
    } else if (!this.isIncludedItem && !this.isBaseItem) {
      this.includedQuantity = 0
    }

    if (this.onIncludedChange) {
      this.onIncludedChange(this)
    }
  }

  @action.bound
  toggleIncludedItem() {
    this.isIncludedItem = !this.isIncludedItem

    // Untoggle base item if included item is untoggled
    if (this.isBaseItem) {
      this.isBaseItem = !this.isBaseItem
    }

    if (this.isIncludedItem && this.includedQuantity === 0) {
      this.includedQuantity = 1
    } else if (!this.isIncludedItem) {
      this.includedQuantity = 0
    }

    if (this.onIncludedChange) {
      this.onIncludedChange(this)
    }
  }

  @action.bound
  setQuantity(value: string) {
    this.includedQuantity = parseInt(value, 10) || 0
  }

  @action.bound
  incrementQuantity() {
    this.includedQuantity += 1
  }

  @action.bound
  decrementQuantity() {
    if (this.includedQuantity <= 0) {
      this.includedQuantity = 0
    } else {
      this.includedQuantity -= 1
    }
  }

  @computed
  get serviceItemStatusMessage() {
    if (this.archived && this.isBaseItem) {
      return '*archived base item'
    } else if (this.archived) {
      return '*archived'
    } else if (this.isBaseItem) {
      return '*base item'
    }

    return ''
  }
}
